import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  DataTable,
  DatePicker,
  DatePickerInput,
  FilterableMultiSelect,
  MultiSelect,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add, Download, Filter } from '@carbon/react/icons';
import { EmptyState } from '@carbon/ibm-products';
import { PageHeader } from 'components/page-header';
import { usePricingEnums } from 'pages/pricing/shared/use-pricing-enums';
import type { Option, Transformation } from 'pages/pricing/shared/types';
import { parseUTCdateTime } from 'utils/dates';
import styles from './transformation-library.module.scss';

const HEADERS = [
  { key: 'name', header: 'Transformation' },
  { key: 'commodity', header: 'Commodity' },
  { key: 'benchmark', header: 'Benchmark' },
  { key: 'ops', header: 'Operations' },
  { key: 'createdBy', header: 'Created by' },
  { key: 'createdAt', header: 'Created' },
  { key: 'updatedAt', header: 'Last updated' },
];

const labelOf = (opts: Option[], value: string | null) =>
  opts.find((o) => o.value === value)?.label ?? value ?? '';

// Backend timestamps are UTC; format them in UTC so the date can't drift a day near midnight.
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  return parseUTCdateTime(iso);
};

const inRange = (
  iso: string | null | undefined,
  from: Date | null,
  to: Date | null
) => {
  if (!from && !to) return true;
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true;
  if (from && t < from.getTime()) return false;
  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (t > end.getTime()) return false;
  }
  return true;
};

interface Props {
  transformations: Transformation[];
  onOpen: (id: number) => void;
  onAdd: () => void;
}

export function TransformationLibrary({
  transformations,
  onOpen,
  onAdd,
}: Props) {
  const { enums } = usePricingEnums();
  const commodityOpts = useMemo(() => enums?.commodities ?? [], [enums]);
  const statusOpts = useMemo(
    () => enums?.transformationStatuses ?? [],
    [enums]
  );

  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [commodities, setCommodities] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [benchmarks, setBenchmarks] = useState<string[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [updatedFrom, setUpdatedFrom] = useState<Date | null>(null);
  const [updatedTo, setUpdatedTo] = useState<Date | null>(null);

  const allBenchmarks = useMemo(
    () =>
      Array.from(
        new Set(transformations.map((t) => t.benchmark).filter(Boolean))
      ).sort(),
    [transformations]
  ) as string[];
  const allUsers = useMemo(
    () =>
      Array.from(
        new Set(
          transformations
            .flatMap((t) => [t.createdBy, t.updatedBy])
            .filter(Boolean)
        )
      ).sort(),
    [transformations]
  ) as string[];

  const createdActive = !!(createdFrom || createdTo);
  const updatedActive = !!(updatedFrom || updatedTo);
  const filterCount = [
    commodities.length > 0,
    statuses.length > 0,
    benchmarks.length > 0,
    users.length > 0,
    createdActive,
    updatedActive,
  ].filter(Boolean).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transformations.filter((t) => {
      const commodity = labelOf(commodityOpts, t.commodity);
      const status = labelOf(statusOpts, t.status);
      if (commodities.length > 0 && !commodities.includes(commodity))
        return false;
      if (statuses.length > 0 && !statuses.includes(status)) return false;
      if (benchmarks.length > 0 && !benchmarks.includes(t.benchmark ?? ''))
        return false;
      if (
        users.length > 0 &&
        !users.includes(t.createdBy ?? '') &&
        !users.includes(t.updatedBy ?? '')
      )
        return false;
      if (!inRange(t.createdAt, createdFrom, createdTo)) return false;
      if (!inRange(t.updatedAt, updatedFrom, updatedTo)) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        commodity.toLowerCase().includes(q) ||
        (t.benchmark ?? '').toLowerCase().includes(q)
      );
    });
  }, [
    transformations,
    commodityOpts,
    statusOpts,
    search,
    commodities,
    statuses,
    benchmarks,
    users,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
  ]);

  const rows = useMemo(
    () =>
      filtered.slice((page - 1) * pageSize, page * pageSize).map((t) => ({
        id: String(t.id),
        name: t.name,
        commodity: labelOf(commodityOpts, t.commodity),
        benchmark: t.benchmark ?? '',
        ops: t.steps.length,
        createdBy: t.createdBy ?? '',
        createdAt: fmtDate(t.createdAt),
        updatedAt: fmtDate(t.updatedAt),
      })),
    [filtered, page, pageSize, commodityOpts]
  );

  const resetFilters = () => {
    setCommodities([]);
    setStatuses([]);
    setBenchmarks([]);
    setUsers([]);
    setCreatedFrom(null);
    setCreatedTo(null);
    setUpdatedFrom(null);
    setUpdatedTo(null);
    setSearch('');
    setPage(1);
  };

  const exportCsv = () => {
    const head = HEADERS.map((h) => h.header);
    const body = filtered.map((t) => [
      t.name,
      labelOf(commodityOpts, t.commodity),
      t.benchmark ?? '',
      t.steps.length,
      t.createdBy ?? '',
      fmtDate(t.createdAt),
      fmtDate(t.updatedAt),
    ]);
    const csv = [head, ...body]
      .map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'price-transformations.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleLabel =
    filterCount > 0
      ? `${filterCount} filter${filterCount > 1 ? 's' : ''} applied`
      : showFilters
        ? 'Hide filters'
        : 'Show filters';

  return (
    <div className={styles.list}>
      <PageHeader
        breadcrumbs={[]}
        title="Price Transformations"
        actions={
          <Button kind="primary" size="lg" renderIcon={Add} onClick={onAdd}>
            Add Transformation
          </Button>
        }
      />

      <section className={styles.main}>
        {showFilters && (
          <aside className={styles.filterPanel}>
            <div className={styles.filterHeader}>
              <Button
                kind="ghost"
                size="sm"
                disabled={filterCount === 0 && search === ''}
                onClick={resetFilters}
              >
                Reset filters
              </Button>
            </div>

            <Accordion>
              <AccordionItem title="Dates" open>
                <div className={styles.dateGroup}>
                  <span className={styles.groupLabel}>Created</span>
                  <DatePicker
                    datePickerType="range"
                    value={[createdFrom, createdTo].filter(Boolean) as Date[]}
                    onChange={(dates: Date[]) => {
                      setCreatedFrom(dates[0] ?? null);
                      setCreatedTo(dates[1] ?? null);
                      setPage(1);
                    }}
                  >
                    <DatePickerInput
                      id="tx-created-from"
                      placeholder="mm/dd/yyyy"
                      labelText="From"
                      size="sm"
                    />
                    <DatePickerInput
                      id="tx-created-to"
                      placeholder="mm/dd/yyyy"
                      labelText="To"
                      size="sm"
                    />
                  </DatePicker>
                </div>
                <div className={styles.dateGroup}>
                  <span className={styles.groupLabel}>Last updated</span>
                  <DatePicker
                    datePickerType="range"
                    value={[updatedFrom, updatedTo].filter(Boolean) as Date[]}
                    onChange={(dates: Date[]) => {
                      setUpdatedFrom(dates[0] ?? null);
                      setUpdatedTo(dates[1] ?? null);
                      setPage(1);
                    }}
                  >
                    <DatePickerInput
                      id="tx-updated-from"
                      placeholder="mm/dd/yyyy"
                      labelText="From"
                      size="sm"
                    />
                    <DatePickerInput
                      id="tx-updated-to"
                      placeholder="mm/dd/yyyy"
                      labelText="To"
                      size="sm"
                    />
                  </DatePicker>
                </div>
              </AccordionItem>
            </Accordion>

            <Accordion>
              <AccordionItem title="Transformation filters" open>
                <div className={styles.filterGroup}>
                  <MultiSelect
                    id="tx-f-commodity"
                    titleText="Commodity"
                    label="Any commodity"
                    size="sm"
                    items={commodityOpts.map((o) => o.label)}
                    initialSelectedItems={commodities}
                    onChange={({ selectedItems }) => {
                      setCommodities((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <MultiSelect
                    id="tx-f-status"
                    titleText="Status"
                    label="Any status"
                    size="sm"
                    items={statusOpts.map((o) => o.label)}
                    initialSelectedItems={statuses}
                    onChange={({ selectedItems }) => {
                      setStatuses((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FilterableMultiSelect
                    id="tx-f-benchmark"
                    titleText="Benchmark"
                    placeholder="Search benchmarks…"
                    size="sm"
                    items={allBenchmarks}
                    initialSelectedItems={benchmarks}
                    onChange={({ selectedItems }) => {
                      setBenchmarks((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FilterableMultiSelect
                    id="tx-f-users"
                    titleText="Created by"
                    placeholder="Search users…"
                    size="sm"
                    items={allUsers}
                    initialSelectedItems={users}
                    onChange={({ selectedItems }) => {
                      setUsers((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
              </AccordionItem>
            </Accordion>
          </aside>
        )}

        <div className={styles.tableArea}>
          <div className={styles.toolbar}>
            <Button
              kind="ghost"
              renderIcon={Filter}
              onClick={() => setShowFilters((v) => !v)}
            >
              {toggleLabel}
            </Button>
            <Search
              size="lg"
              labelText="Search transformations"
              placeholder="Search transformations…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              onClear={() => setSearch('')}
              className={styles.toolbarSearch}
            />
            <Button
              kind="ghost"
              size="lg"
              hasIconOnly
              renderIcon={Download}
              iconDescription="Export CSV"
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={exportCsv}
            />
          </div>

          <div className={styles.tableContainer}>
            {filtered.length === 0 ? (
              <EmptyState
                size="lg"
                title="No transformations found"
                subtitle={
                  filterCount > 0 || search.length > 0
                    ? 'Try adjusting your filters or search terms.'
                    : 'You don’t have any transformations yet. Add one to get started.'
                }
                action={
                  filterCount > 0 || search.length > 0
                    ? { text: 'Reset filters', onClick: resetFilters }
                    : { text: 'Add Transformation', onClick: onAdd }
                }
              />
            ) : (
              <DataTable rows={rows} headers={HEADERS} isSortable>
                {({
                  rows: tableRows,
                  headers,
                  getTableProps,
                  getHeaderProps,
                  getRowProps,
                }) => (
                  <Table {...getTableProps()} size="lg">
                    <TableHead>
                      <TableRow>
                        {headers.map((h) => {
                          const { key, ...rest } = getHeaderProps({
                            header: h,
                          });
                          return (
                            <TableHeader key={key ?? h.key} {...rest}>
                              {h.header}
                            </TableHeader>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map((row) => {
                        const { key, ...rest } = getRowProps({ row });
                        return (
                          <TableRow key={key ?? row.id} {...rest}>
                            {row.cells.map((cell) => {
                              if (cell.info.header === 'name') {
                                return (
                                  <TableCell key={cell.id}>
                                    <button
                                      type="button"
                                      className={styles.nameLink}
                                      onClick={() => onOpen(Number(row.id))}
                                    >
                                      {cell.value}
                                    </button>
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={cell.id}>
                                  {cell.value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DataTable>
            )}
          </div>

          <Pagination
            backwardText="Previous page"
            forwardText="Next page"
            itemsPerPageText="Transformations per page:"
            totalItems={filtered.length}
            pageSize={pageSize}
            pageSizes={[25, 50, 75, 100]}
            page={page}
            onChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      </section>
    </div>
  );
}
